import {
  SkyBadge,
  SkyButton,
  SkyCard,
  SkyMessageBar,
  SkyProgressBar,
  SkySkeleton,
  SkySkeletonItem,
  SkyTabList,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import { dbtModelCountsOf } from "@shared/types/dbtModelCounts";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import type { DbtJobRunRow, DbtJobView, TransformationGraph } from "@shared/types/transformations";
import { isMalformedRecordId } from "@/components/access/RecordNotFound";
import { EmptyState, ErrorState, PermissionNotice } from "@/components/access/ScreenStates";
import { TopbarActionsPortal } from "@/components/navigation/TopbarActionsPortal";
import { NOT_STARTED_LABEL, formatDuration } from "@/components/runs/runFormatting";
import { BundleFacts } from "@/components/transformations/BundleFacts";
import { ModelGraph } from "@/components/transformations/ModelGraph";
import { ModelHistoryTimeline } from "@/components/transformations/ModelHistoryTimeline";
import { RunDbtJobsDialog } from "@/components/transformations/RunDbtJobsDialog";
import { TestsPanel } from "@/components/transformations/TestsPanel";
import { DbtJobDrawer } from "@/components/transformations/DbtJobDrawer";
import { DbtJobRunsGrid } from "@/components/transformations/DbtJobRunsGrid";
import { PageCrumb } from "@/components/shell/PageCrumb";
import {
  activeStepLine,
  asSentence,
  attributeToGraph,
  attributeToOwnRun,
  ownRunWords,
  formatRows,
  kindLabel,
  phaseWords,
  ranInStep,
  rowsWritten,
  runNotStarted,
  runNowOffReason,
  runNowPageBlock,
  runStartText,
  selectionFor,
  stepTestCountsAreOwn,
  testCountsPhrase,
  testsSkipped,
  transformStepPresentation,
} from "@/components/transformations/transformFormatting";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { useApiErrorNotice } from "@/lib/feedback/useApiErrorNotice";
import { usePermissions } from "@/hooks/use-permissions";
import { useCancelRunById, usePollWhile, RUN_POLL_INTERVAL_MS } from "@/hooks/use-runs";
import {
  DBT_JOB_RUNS_PAGE_SIZES,
  refetchNewestRuns,
  useRefreshDbtJobHistory,
  useDbtJob,
  useTransformationGraph,
  useDbtJobRuns,
  useDbtJobs,
  type DbtJobRunsPage,
} from "@/hooks/use-dbt-jobs";
import { isNotFoundError } from "@/components/access/RecordNotFound";
import { CancelIcon, EditIcon, PlayIcon } from "@/lib/icons";
import { ROUTES, absolutePath } from "@/lib/routes";
import { DBT_SETTINGS_PATH, OPEN_DBT_SETTINGS_LABEL } from "@/lib/transformations/dbtSettings";
import { formatInstant } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { RunningDuration } from "@/components/runs/RunningDuration";

/**
 * One dbt job, seen through the last run (canvas 16; FR-62, UX-5, SEC-17).
 *
 * dbt is invoked once per run with the merged selector, so the graph is
 * instance-wide and the run tiles are run-wide (`graph.run`). What THIS
 * dbt job selected comes from the server (`selection[]` and each node's
 * `selectedBy`, evaluated against the manifest): when known, its nodes carry
 * the "this transformation" outline, a tile sums them, and the Tests tab opens
 * on the tests of its models. When the server could not evaluate the selector
 * (or the row was not in the graph's run), the tile says why and nothing is
 * outlined on a guess.
 *
 * A run in flight never replaces the graph mid-way: the header says Running
 * and follows the newest step on a five-second poll, while the graph stays
 * labelled with the finished run it came from.
 *
 * The five tabs are hash deep links (`#graph`, `#runs`, `#history`, `#tests`,
 * `#bundle`), so a run link can open the tests directly. History (session 7,
 * canvas 18) is the third: this dbt job's models across runs. It is
 * fetched only while its tab is open.
 */

type DetailTab = "graph" | "runs" | "history" | "tests" | "bundle";
const TABS: DetailTab[] = ["graph", "runs", "history", "tests", "bundle"];
const TAB_LABELS: Record<DetailTab, string> = {
  graph: "Graph",
  runs: "Runs",
  history: "History",
  tests: "Tests",
  bundle: "Bundle",
};

function tabFromHash(): DetailTab {
  const hash = window.location.hash.replace(/^#/, "");
  return (TABS as string[]).includes(hash) ? (hash as DetailTab) : "graph";
}

function HeaderSkeleton() {
  return (
    <SkySkeleton testId="transformation-detail-loading" aria-busy="true">
      <div className="rounded-lg border border-border bg-card p-4">
        <SkySkeletonItem size={20} width={340} />
        <div className="h-4" />
        <div className="grid grid-cols-[repeat(2,minmax(0,1fr))] gap-4 sm:grid-cols-5">
          {[0, 1, 2, 3, 4].map((cell) => (
            <div key={cell}>
              <SkySkeletonItem size={9} width={70} />
              <div className="h-2" />
              <SkySkeletonItem size={16} width={90} />
            </div>
          ))}
        </div>
      </div>
    </SkySkeleton>
  );
}

function Kpi({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <div className="min-w-0 rounded-md border border-border bg-background px-3 py-2 [overflow-wrap:anywhere]">
      <SkyText size={200} color="muted" block className="uppercase tracking-wider">
        {label}
      </SkyText>
      <SkyText size={500} weight="semibold" font="numeric">
        {value}
      </SkyText>
      {detail ? (
        <SkyText size={200} color="muted" className="ml-1">
          {detail}
        </SkyText>
      ) : null}
    </div>
  );
}

type GraphRun = NonNullable<TransformationGraph["run"]>;
type Presentation = ReturnType<typeof transformStepPresentation>;
type Attribution = ReturnType<typeof attributeToGraph>;
type Selection = ReturnType<typeof selectionFor>;
type Navigate = (to: string) => void;

/** What the page says about this row against the newest step and the graph's run. */
function rowFacts(
  row: DbtJobView,
  graphData: TransformationGraph | undefined,
  latestStep: DbtJobRunRow | null,
) {
  const graphRun = graphData?.run ?? null;
  // The newest step is the INSTANCE's; its outcome is this row's only when the
  // step named the row (or, for a step that named none, when the row would run).
  const rowInLatest = ranInStep(row, latestStep);
  // Round 7 (backlog "The dbt job page's header by its own last run", walk D4 follow-up): a job
  // the newest step did not include reads its own last run, as the Transformations grid does.
  const ownRun = latestStep && !rowInLatest ? attributeToOwnRun(row, graphData) : null;
  const ownRunText = ownRun ? ownRunWords(ownRun) : null;
  // What this row's selector selected in the GRAPH's run, when that is known.
  // "In the graph's run" is decided by id when the server sent a selection (a renamed
  // dbt job keeps its attribution, review N-5), by name otherwise.
  const selection = selectionFor(row.id, graphData);
  const inGraphRun = graphData?.selection
    ? selection !== undefined
    : Boolean(graphRun && ranInStep(row, graphRun));
  const attribution = graphData && inGraphRun ? attributeToGraph(row, graphData) : null;
  // The outline, the tiles and the Tests scope describe the selector AS THAT RUN
  // USED IT. After an edit the page would otherwise look as if the save did
  // nothing (owner feedback), so a changed selector is said out loud.
  const selectorChanged =
    graphRun !== null && selection?.selector != null && selection.selector !== row.dbtSelector;
  return {
    graphRun,
    rowInLatest,
    ownRun,
    ownRunText,
    selection,
    inGraphRun,
    attribution,
    selectorChanged,
  };
}

type RowFacts = ReturnType<typeof rowFacts>;

/** Round 6, T5: the summary's succeeded count, else the step's (an older row: reported minus failed). */
function modelsOkOf(graphRun: GraphRun | null): number | null {
  if (!graphRun) return null;
  return graphRun.models?.succeeded ?? dbtModelCountsOf(graphRun)?.built ?? null;
}

/** The line that says why this job's Run now is switched off (#1293). */
const RUN_NOW_REASON_ID = "transformation-run-now-reason";

/** #1293: why Run now waits on the list of dbt jobs (the project's own gate is `runNowPageBlock`). */
function jobsListBlock(all: { isPending: boolean; isError: boolean }): string | null {
  if (all.isPending) return "Switched off until the dbt jobs have loaded.";
  if (all.isError) {
    return "Switched off because the list of dbt jobs could not be read. Refresh to try again.";
  }
  return null;
}

interface DetailTopbarArgs {
  canCancel: boolean;
  canStart: boolean;
  canWrite: boolean;
  latest: Presentation;
  latestStep: DbtJobRunRow | null;
  cancelling: boolean;
  runNowDisabled: boolean;
  /** #1293: why this job's Run now is off, said beside the button. */
  runNowReasonId: string | undefined;
  onCancel: () => void;
  onRunNow: () => void;
  /** The instance-wide "Run all dbt jobs now", kept beside this job's own Run now (#1293). */
  runAllDisabled: boolean;
  runAllReasonId: string | undefined;
  onRunAll: () => void;
  onEdit: () => void;
}

function detailTopbar(args: DetailTopbarArgs): ReactNode {
  return (
    <TopbarActionsPortal>
      {args.canCancel && args.latest.isActive && args.latestStep ? (
        <SkyButton
          appearance="secondary"
          icon={CancelIcon}
          loading={args.cancelling}
          onClick={args.onCancel}
          testId="transformation-cancel-run"
        >
          Cancel run
        </SkyButton>
      ) : null}
      {args.canStart ? (
        <SkyButton
          appearance="secondary"
          icon={PlayIcon}
          onClick={args.onRunAll}
          disabled={args.runAllDisabled}
          aria-describedby={args.runAllReasonId}
          testId="run-transformations-all"
        >
          Run all dbt jobs now
        </SkyButton>
      ) : null}
      {args.canStart ? (
        <SkyButton
          appearance="primary"
          icon={PlayIcon}
          onClick={args.onRunNow}
          // As on the list: not live until the binding (the graph's `bundle`)
          // has answered, so the dialog never says "not bound" about a
          // binding that has not loaded or could not be read.
          disabled={args.runNowDisabled}
          aria-describedby={args.runNowReasonId}
          testId="run-transformations-now"
        >
          Run now
        </SkyButton>
      ) : null}
      {args.canWrite ? (
        <SkyButton
          appearance="secondary"
          icon={EditIcon}
          onClick={args.onEdit}
          testId="transformation-edit"
        >
          Edit
        </SkyButton>
      ) : null}
    </TopbarActionsPortal>
  );
}

function selectorText(row: DbtJobView): ReactNode {
  if (row.dbtSelector) {
    return (
      <SkyText size={200} font="monospace" color="muted">
        {row.dbtSelector}
      </SkyText>
    );
  }
  return (
    <SkyText
      size={200}
      color="muted"
      title="A blank selector is left out of the run, never widened to the whole project."
    >
      (no selector: left out of runs)
    </SkyText>
  );
}

function headerTitleRow(
  row: DbtJobView,
  facts: RowFacts,
  namesById: Map<string, string>,
  zone: string,
): ReactNode {
  const { graphRun, selection } = facts;
  return (
    <div className="flex flex-wrap items-center gap-2 pb-3">
      <SkyText as="h2" size={400} weight="semibold">
        {row.name}
      </SkyText>
      {selectorText(row)}
      {facts.selectorChanged && graphRun ? (
        <SkyText size={200} weight="medium" testId="transformation-selector-changed">
          · selector changed since the run that started{" "}
          <Link
            href={absolutePath(`/runs/${graphRun.runId}`)}
            className="underline underline-offset-2"
            title={graphRun.runId}
          >
            {runStartText(graphRun, zone, "short", { named: true })}
          </Link>{" "}
          (it ran {selection?.selector}). The graph and tiles show what that run selected.
        </SkyText>
      ) : null}
      {row.dependsOn.length > 0 ? (
        <SkyText size={200} color="muted">
          · runs after {row.dependsOn.map((dep) => namesById.get(dep) ?? dep).join(", ")}
        </SkyText>
      ) : null}
      <span className="ml-auto flex items-center gap-2">
        <SkyBadge appearance="tint" color={row.enabled ? "success" : "subtle"}>
          {row.enabled ? "Enabled" : "Disabled"}
        </SkyBadge>
        <SkyBadge appearance="tint" color={row.runTests ? "success" : "subtle"}>
          {row.runTests ? "Run tests on" : "Run tests off"}
        </SkyBadge>
      </span>
    </div>
  );
}

interface StateRowArgs {
  row: DbtJobView;
  facts: RowFacts;
  latest: Presentation;
  latestStep: DbtJobRunRow | null;
  zone: string;
}

function stateBadge({ row, facts, latest, latestStep, zone }: StateRowArgs): ReactNode {
  const { ownRun, ownRunText } = facts;
  if (ownRun && ownRunText) {
    return (
      <>
        <SkyBadge appearance="tint" color={ownRunText.color}>
          {ownRunText.word}
        </SkyBadge>
        <SkyText size={200} testId="transformation-own-run">
          {ownRunText.line ? `${ownRunText.line} · ` : ""}its own run{" "}
          <Link
            href={absolutePath(`/runs/${ownRun.runId}`)}
            className="font-mono underline underline-offset-2"
          >
            {ownRun.runId}
          </Link>
          {ownRun.at ? `, ${formatInstant(ownRun.at, zone, "short", { named: true })}` : ""}
        </SkyText>
      </>
    );
  }
  if (latestStep && !facts.rowInLatest) {
    // The instance's newest step did not include this row and it has no run of its
    // own: name the step, never attribute its outcome.
    return (
      <>
        <SkyBadge appearance="tint" color="subtle">
          Not in the last run
        </SkyBadge>
        <SkyText size={200} color="muted" testId="transformation-not-in-last-run">
          The last run,{" "}
          <Link
            href={absolutePath(`/runs/${latestStep.runId}`)}
            className="font-mono underline underline-offset-2"
          >
            {latestStep.runId}
          </Link>
          {runNotStarted(latestStep)
            ? ", not started yet,"
            : `, started ${runStartText(latestStep, zone, "short", { named: true })},`}{" "}
          did not include this dbt job
          {runNowOffReason(row) === null ? ". It joins the next one." : "."}
        </SkyText>
      </>
    );
  }
  return (
    <SkyBadge appearance="tint" color={latest.color}>
      {latest.label}
    </SkyBadge>
  );
}

/** Whether the last run line has a duration to say: a finished step's, or a running one's. */
function hasDurationText(step: DbtJobRunRow): boolean {
  if (step.durationSeconds !== null && Number.isFinite(step.durationSeconds)) return true;
  return step.stepStatus === "running" && step.startedAt !== null;
}

function lastRunLine({ facts, latest, latestStep, zone }: StateRowArgs): ReactNode {
  if (latestStep && facts.rowInLatest) {
    return (
      <SkyText size={200}>
        Last run{" "}
        <Link
          href={absolutePath(`/runs/${latestStep.runId}`)}
          className="font-mono underline underline-offset-2"
        >
          {latestStep.runId}
        </Link>{" "}
        · {kindLabel(latestStep.kind).toLowerCase()} ·{" "}
        {runNotStarted(latestStep)
          ? NOT_STARTED_LABEL
          : `started ${runStartText(latestStep, zone, "short", { named: true })}`}
        {/* #1207: a run with no duration yet (queued, or its dbt step not started) says
            nothing here, never a dash inside the line. */}
        {hasDurationText(latestStep) ? (
          <>
            {" · "}
            <RunningDuration
              seconds={latestStep.durationSeconds}
              runningSince={latestStep.stepStatus === "running" ? latestStep.startedAt : null}
            />
          </>
        ) : null}
        {latest.isActive && latestStep.phase
          ? ` · ${phaseWords(latestStep.phase).toLowerCase()}`
          : ""}
      </SkyText>
    );
  }
  if (latestStep) return null;
  return (
    <SkyText size={200} color="muted">
      This dbt job has not been part of a finished run yet.
    </SkyText>
  );
}

function stateRow(args: StateRowArgs): ReactNode {
  const { facts, latest, latestStep } = args;
  return (
    <div className="flex flex-wrap items-center gap-2 pb-3" data-testid="transformation-state">
      {stateBadge(args)}
      {lastRunLine(args)}
      {latestStep?.error && facts.rowInLatest ? (
        <SkyText size={200} color="muted" block className="basis-full">
          {latestStep.error}
        </SkyText>
      ) : null}
      {latest.isActive && facts.rowInLatest ? (
        <SkyProgressBar
          value={latestStep?.phase ? 50 : 10}
          thickness="small"
          label="Progress of the dbt run"
          className="w-40"
        />
      ) : null}
    </div>
  );
}

/**
 * #1207: what the tile says when it cannot name this job's models, as a value and a sentence. The
 * graph and the other tiles are the whole run's, so the sentence says so. The server's reason for
 * an unevaluated selector names internals (the manifest summary), so it is not printed.
 */
function thisJobUnknown(
  inGraphRun: boolean,
  selection: Selection,
): { value: string; detail: string } {
  if (!inGraphRun) {
    return {
      value: "Not in this run",
      detail: "The tiles beside it are for a run it was not part of.",
    };
  }
  if (selection && !selection.known) {
    return {
      value: "Not known",
      detail: "Its selector could not be matched to models. The other tiles are for the whole run.",
    };
  }
  return {
    value: "Not known",
    detail:
      "Which models this dbt job selected is not known. The other tiles are for the whole run.",
  };
}

function thisJobKpi(
  row: DbtJobView,
  graphRun: GraphRun,
  attribution: Attribution,
  inGraphRun: boolean,
  selection: Selection,
): ReactNode {
  if (attribution) {
    // #1386: the tests on its models count as its own only when they are (a job with tests off
    // in a step it shared never shows another job's tests), the rule the card and the grid read.
    const tests = stepTestCountsAreOwn(row, graphRun)
      ? testCountsPhrase(attribution.testCounts)
      : "Tests off";
    return (
      <Kpi
        label="This dbt job"
        value={`${attribution.models.length} model${attribution.models.length === 1 ? "" : "s"}`}
        detail={[
          attribution.outcome?.label,
          attribution.rows !== null ? formatRows(attribution.rows) : null,
          tests,
        ]
          .filter(Boolean)
          .join(" · ")}
      />
    );
  }
  const unknown = thisJobUnknown(inGraphRun, selection);
  return <Kpi label="This dbt job" value={unknown.value} detail={unknown.detail} />;
}

/** #1207 round 1: "3 succeeded · 1 failed", the page's word for success, never dbt's "ok". */
function modelsKpiDetail(graphRun: GraphRun): string | undefined {
  const ok = modelsOkOf(graphRun);
  const lead = ok === null ? "" : `${ok} succeeded · `;
  if (graphRun.models) {
    return `${lead}${graphRun.models.failed} failed · ${graphRun.models.skipped} skipped`;
  }
  return graphRun.modelsFailed !== null ? `${lead}${graphRun.modelsFailed} failed` : undefined;
}

function testsKpiDetail(graphRun: GraphRun): string | undefined {
  if (graphRun.testsTotal !== null) {
    const skipped = testsSkipped(graphRun) ? ` · ${testsSkipped(graphRun)} skipped` : "";
    return `${graphRun.testsSucceeded ?? "—"} passed · ${graphRun.testsFailed ?? graphRun.tests?.failed ?? "—"} failed${skipped}`;
  }
  return graphRun.testsFailed !== null ? `${graphRun.testsFailed} failed` : undefined;
}

/**
 * #1074 review: the header reads this job's own run while the tiles read the project's last
 * run, which did not include it. The caption names that run, so the two never read as one.
 */
function kpiCaption(graphRun: GraphRun, facts: RowFacts, zone: string): ReactNode {
  if (!facts.ownRun || facts.ownRun.runId === graphRun.runId) return null;
  return (
    <SkyText size={200} color="muted" block className="pb-2" testId="transformation-kpis-run">
      The figures below are from the project&apos;s last run{" "}
      <Link
        href={absolutePath(`/runs/${graphRun.runId}`)}
        className="font-mono underline underline-offset-2"
      >
        {graphRun.runId}
      </Link>
      {`, ${formatInstant(graphRun.startedAt ?? graphRun.createdAt, zone, "short", { named: true })}`}
      , which did not include this dbt job.
    </SkyText>
  );
}

function kpiGrid(
  row: DbtJobView,
  graphRun: GraphRun,
  facts: RowFacts,
  rowsSum: number | null,
): ReactNode {
  const modelsOk = modelsOkOf(graphRun);
  return (
    <div
      className="grid grid-cols-[repeat(2,minmax(0,1fr))] gap-3 sm:grid-cols-3 2xl:grid-cols-6"
      data-testid="transformation-kpis"
    >
      <div data-testid="transformation-kpi-this">
        {thisJobKpi(row, graphRun, facts.attribution, facts.inGraphRun, facts.selection)}
      </div>
      <Kpi
        label="Models, last run"
        // When the summary is unreadable the step's own counts stand in:
        // reported minus failed, never the total shown as "ok".
        value={modelsOk === null ? "—" : String(modelsOk)}
        detail={modelsKpiDetail(graphRun)}
      />
      <Kpi
        label="Rows written"
        value={rowsSum === null ? "—" : rowsSum.toLocaleString()}
        detail="sum of model rows"
      />
      <Kpi
        label="Tests, last run"
        // The summary's counts (`testsTotal`/`testsSucceeded`); when it
        // was unreadable only the step's failed count is known, and the
        // total is never shown as that number.
        value={graphRun.testsTotal === null ? "—" : String(graphRun.testsTotal)}
        detail={testsKpiDetail(graphRun)}
      />
      <Kpi
        label="Duration"
        value={formatDuration(graphRun.durationSeconds)}
        // #1207 round 1: what an active run is doing, in words; a finished run's result is the
        // Result tile's.
        detail={
          graphRun.stepStatus === "running" || graphRun.stepStatus === "pending"
            ? activeStepLine(graphRun)
            : undefined
        }
      />
      {/* #1207: the run's result in the header's words ("Failed", "Could not clone"), never its
          exit class or image tag. */}
      <Kpi label="Result" value={transformStepPresentation(graphRun).label} />
    </div>
  );
}

/**
 * The sentence says who acts, for every reader. The button only for one who can open the dbt
 * repository tab (`instance.binding.read`, Instance admin and above, #1106).
 */
function unboundBar(navigate: Navigate, canOpenDestination: boolean): ReactNode {
  return (
    <SkyMessageBar
      intent="warning"
      layout="multiline"
      title="No dbt repository is bound to this instance"
      message="This dbt job is saved but cannot run until an instance admin or above binds a repository on the Destination screen's dbt repository tab."
      testId="transformation-unbound"
      actions={
        canOpenDestination ? (
          <SkyButton
            appearance="secondary"
            size="small"
            testId="transformation-unbound-open-destination"
            // Straight to the dbt repository tab the message names (Session 10 P5).
            onClick={() => navigate(absolutePath(DBT_SETTINGS_PATH))}
          >
            {OPEN_DBT_SETTINGS_LABEL}
          </SkyButton>
        ) : undefined
      }
    />
  );
}

function graphFailedBar(graphRun: GraphRun, navigate: Navigate): ReactNode {
  const failed = graphRun.modelsFailed;
  const runError = asSentence(graphRun.error ?? "The dbt run failed in the run phase");
  const modelsWord = failed === 1 ? "model" : "models";
  return (
    <SkyMessageBar
      intent="error"
      layout="multiline"
      // From S7 an invocation-level warehouse error exits `run` with no model
      // finished (modelsFailed 0 or null): no node is red, so the bar says so.
      title={
        failed
          ? `${failed} ${modelsWord} failed in the last run`
          : "The last run failed before any model finished"
      }
      // `error` is the runner's sentence and, from S7, already ends with the
      // classified cause: it is shown once, never joined with cause.sentence.
      message={
        failed
          ? `${runError} The failed model is red below and the models built from it were skipped. Open the model for its cause. skyData never shows the SQL or dbt's own message.`
          : `${runError} skyData never shows the SQL or dbt's own message.`
      }
      testId="model-graph-failed"
      actions={
        <SkyButton
          appearance="secondary"
          size="small"
          onClick={() => navigate(absolutePath(`/runs/${graphRun.runId}`))}
        >
          Open run {graphRun.runId}
        </SkyButton>
      }
    />
  );
}

/** #1293: the one reason, if any, that no run builds this job's models (a role-neutral fact). */
function noRunCauseOf(row: DbtJobView, repoBound: boolean): GraphTabArgs["noRunCause"] {
  if (!row.enabled) return "disabled";
  if (!row.dbtSelector?.trim()) return "no selector";
  return repoBound ? null : "no repository";
}

const NO_RUN_CAUSE_WORDS = {
  disabled: "This dbt job is disabled, so it does not run.",
  "no selector": "This dbt job has no selector, so it does not run.",
  "no repository": "No dbt repository is set, so no run builds its models.",
} as const;

/** The empty graph's sentence: the real cause first, Run now named only to a reader who has it. */
function emptyGraphMessage(args: GraphTabArgs): string {
  if (args.noRunCause) {
    return `The graph appears after a dbt job has run in this project. ${NO_RUN_CAUSE_WORDS[args.noRunCause]}`;
  }
  const base = "The graph is built from the last run's dbt project.";
  const tail = "the models, their dependencies and their test results appear here.";
  return args.canStart && !args.runNowBlocked
    ? `${base} Run this dbt job now, and ${tail}`
    : `${base} Once this dbt job has run, ${tail}`;
}

interface GraphTabArgs {
  graph: UseQueryResult<TransformationGraph>;
  graphRun: GraphRun | null;
  attribution: Attribution;
  latest: Presentation;
  canStart: boolean;
  /** #1293: Run now is off for any reason (the topbar's own answer), so the empty state offers none. */
  runNowBlocked: boolean;
  /** #1293: why no run builds this job's models today, or null when none of these hold. */
  noRunCause: "disabled" | "no selector" | "no repository" | null;
  onRunNow: () => void;
  navigate: Navigate;
}

function graphReady(graphData: TransformationGraph, args: GraphTabArgs): ReactNode {
  const { graphRun } = args;
  return (
    <>
      {graphData.artifacts.storageError ? (
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          title="The run's artifacts could not be read"
          message={`Storage answered ${graphData.artifacts.storageError}, so the graph shows the nodes the run reported and no edges. The Destination's storage identity needs read access to the staging container.`}
          testId="model-graph-storage-warning"
        />
      ) : null}
      {graphRun?.exitClass === "run" ? graphFailedBar(graphRun, args.navigate) : null}
      {graphRun && args.latest.isActive ? (
        <SkyText size={200} color="muted" testId="model-graph-stale-note">
          The graph is from run {graphRun.runId}, the last one that finished. The run in flight
          replaces it when it lands.
        </SkyText>
      ) : null}
      <ModelGraph graph={graphData} highlight={args.attribution?.nodeIds} />
    </>
  );
}

function graphTab(args: GraphTabArgs): ReactNode {
  const { graph } = args;
  const graphData = graph.data;
  if (graph.isPending) {
    return (
      <div
        className="grid h-60 place-items-center rounded-lg border border-border bg-card"
        aria-busy="true"
        data-testid="model-graph-loading"
      >
        <SkyText size={200} color="muted" role="status">
          Loading the model graph…
        </SkyText>
      </div>
    );
  }
  if (graph.isError || !graphData) {
    return (
      <ErrorState
        title="We could not load the model graph"
        error={graph.error}
        onRetry={() => void graph.refetch()}
      />
    );
  }
  if (graphData.nodes.length === 0) {
    return (
      <EmptyState
        title="No model graph yet"
        message={emptyGraphMessage(args)}
        action={
          args.canStart && !args.runNowBlocked ? (
            <SkyButton
              appearance="primary"
              icon={PlayIcon}
              onClick={args.onRunNow}
              testId="run-transformations-now-empty"
            >
              Run now
            </SkyButton>
          ) : undefined
        }
      />
    );
  }
  return graphReady(graphData, args);
}

function runsTab(
  runs: UseQueryResult<DbtJobRunsPage>,
  runsLimit: number,
  setRunsLimit: (limit: number) => void,
): ReactNode {
  return (
    <>
      <DbtJobRunsGrid rows={runs.data?.rows ?? []} isLoading={runs.isPending} />
      {runs.data && runs.data.rows.length >= runsLimit && runsLimit < 100 ? (
        <SkyButton
          appearance="secondary"
          className="self-start"
          onClick={() =>
            setRunsLimit(DBT_JOB_RUNS_PAGE_SIZES.find((size) => size > runsLimit) ?? 100)
          }
          testId="transformation-runs-load-more"
        >
          Load more runs
        </SkyButton>
      ) : null}
    </>
  );
}

/** The Tests and Bundle tabs: a status line while the graph loads, its error, else the panel. */
function graphReadTab(
  graph: UseQueryResult<TransformationGraph>,
  what: string,
  ready: (graphData: TransformationGraph) => ReactNode,
): ReactNode {
  if (graph.isPending) {
    return (
      <SkyText size={200} color="muted" role="status">
        {`Loading ${what}…`}
      </SkyText>
    );
  }
  if (graph.isError || !graph.data) {
    return (
      <ErrorState
        title={`We could not load ${what}`}
        error={graph.error}
        onRetry={() => void graph.refetch()}
      />
    );
  }
  return ready(graph.data);
}

interface LoadStateArgs {
  transformation: UseQueryResult<DbtJobView>;
  breadcrumb: (name: string) => ReactNode;
  navigate: Navigate;
  /** #1652: the id is not a uuid, so nothing was asked for. */
  idMalformed: boolean;
}

/** The page before the row is in hand: loading, not found, or a failed read; null once it is. */
function rowLoadState({
  transformation,
  breadcrumb,
  navigate,
  idMalformed,
}: LoadStateArgs): ReactNode {
  const notFound = idMalformed || (transformation.isError && isNotFoundError(transformation.error));

  if (transformation.isPending && !idMalformed) {
    return (
      <>
        {breadcrumb("…")}
        <HeaderSkeleton />
      </>
    );
  }

  if (notFound) {
    return (
      <>
        {breadcrumb("Not found")}
        <EmptyState
          title="This dbt job does not exist"
          message="It may have been deleted, or the link belongs to another instance. The list has every dbt job this instance holds."
          action={
            <SkyButton
              appearance="secondary"
              onClick={() =>
                navigate(absolutePath(`${ROUTES.transformations}?view=transformations`))
              }
            >
              Back to Transformations
            </SkyButton>
          }
        />
      </>
    );
  }

  if (transformation.isError || !transformation.data) {
    return (
      <>
        {breadcrumb("…")}
        <ErrorState
          title="We could not load this dbt job"
          error={transformation.error}
          onRetry={() => void transformation.refetch()}
        />
      </>
    );
  }
  return null;
}

interface TabPanelArgs extends GraphTabArgs {
  tab: DetailTab;
  row: DbtJobView;
  runs: UseQueryResult<DbtJobRunsPage>;
  runsLimit: number;
  setRunsLimit: (limit: number) => void;
}

function tabPanelContent(args: TabPanelArgs): ReactNode {
  const { tab, row, graph, attribution, runs, runsLimit, setRunsLimit } = args;
  return (
    <>
      {tab === "graph" ? graphTab(args) : null}

      {tab === "runs" ? runsTab(runs, runsLimit, setRunsLimit) : null}

      {tab === "history" ? <ModelHistoryTimeline dbtJobId={row.id} dbtJobName={row.name} /> : null}

      {tab === "tests"
        ? graphReadTab(graph, "the last run's tests", (data) => (
            <TestsPanel
              graph={data}
              scope={attribution ? { name: row.name, nodeIds: attribution.nodeIds } : undefined}
            />
          ))
        : null}

      {tab === "bundle"
        ? graphReadTab(graph, "the bundle", (data) => <BundleFacts graph={data} />)
        : null}
    </>
  );
}

export default function DbtJobDetailPage({ id }: { id: string }) {
  const zone = useDisplayTz();
  const [, navigate] = useLocation();
  const { canAny, isResolved } = usePermissions();
  const notice = useNotice();
  const showApiError = useApiErrorNotice();

  const canRead = canAny("dbt_jobs.read");
  const canWrite = canAny("dbt_jobs.write");
  const canStart = canAny("runs.start");
  const canCancel = canAny("runs.cancel");

  // #1652: an id that is not a uuid is never asked for; the page reads not found.
  const idMalformed = isMalformedRecordId(id);
  const transformation = useDbtJob(canRead && !idMalformed ? id : null);
  const all = useDbtJobs(canRead);
  const graph = useTransformationGraph(canRead);
  const [runsLimit, setRunsLimit] = useState<number>(DBT_JOB_RUNS_PAGE_SIZES[0]);
  const runs = useDbtJobRuns(runsLimit, canRead);
  const cancelRun = useCancelRunById();

  const [tab, setTab] = useState<DetailTab>(() => tabFromHash());
  const [editing, setEditing] = useState(false);
  const [runNowOpen, setRunNowOpen] = useState(false);
  const [runAllOpen, setRunAllOpen] = useState(false);

  useEffect(() => {
    const onHashChange = () => setTab(tabFromHash());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const selectTab = (next: DetailTab) => {
    setTab(next);
    window.history.replaceState(window.history.state, "", `#${next}`);
  };

  const latestStep = runs.data?.rows[0] ?? null;
  const latest = transformStepPresentation(latestStep);

  // The header follows a step in flight; the graph is not re-read until it lands. #1419: every
  // newest-runs read, so an open Run dialog's own queue read (20 rows) follows it too when History
  // reads a larger page.
  const queryClient = useQueryClient();
  usePollWhile(latest.isActive, RUN_POLL_INTERVAL_MS, () => {
    void refetchNewestRuns(queryClient);
  });

  // The History tab is not polled: when the step in flight lands, its column's
  // "Running" cells are refetched once (review N5).
  const refreshHistory = useRefreshDbtJobHistory();
  const wasActive = useRef(latest.isActive);
  useEffect(() => {
    if (wasActive.current && !latest.isActive) void refreshHistory(id);
    wasActive.current = latest.isActive;
  }, [latest.isActive, id, refreshHistory]);

  const namesById = useMemo(
    () => new Map((all.data ?? []).map((row) => [row.id, row.name])),
    [all.data],
  );

  const doCancel = async (): Promise<void> => {
    if (!latestStep) return;
    try {
      await cancelRun.mutateAsync(latestStep.runId);
      notice.success(`Run ${latestStep.runId} was cancelled.`);
      void runs.refetch();
    } catch (error) {
      showApiError(error, "The run could not be cancelled.");
    }
  };

  if (!isResolved) return <HeaderSkeleton />;
  if (!canRead) return <PermissionNotice permission="dbt_jobs.read" />;

  // The trail is the shell's breadcrumb row (post-release 607e63a); the page names it.
  const breadcrumb = (name: string) => <PageCrumb label={name} />;

  const loadState = rowLoadState({ transformation, breadcrumb, navigate, idMalformed });
  if (loadState !== null || !transformation.data) return loadState;

  const row: DbtJobView = transformation.data;
  const graphData = graph.data;
  const rowsSum = graphData ? rowsWritten(graphData) : null;
  const bundle = graphData?.bundle;
  const repoBound = bundle?.repoBound ?? false;
  const facts = rowFacts(row, graphData, latestStep);
  const { graphRun, attribution } = facts;
  const openRunNow = () => setRunNowOpen(true);
  const pageBlock =
    runNowPageBlock(
      graph.isPending ? "loading" : graph.isError ? "error" : "ready",
      bundle !== undefined && !bundle.repoBound,
    ) ?? jobsListBlock(all);
  const runNowReason = pageBlock ?? runNowOffReason(row, canWrite);
  // Waiting on a query is not a reason to print: the button is off and the line appears only for a
  // cause that stays (an error, no repository, the job itself).
  const runNowReasonShown = canStart && runNowReason !== null && !graph.isPending && !all.isPending;
  const backToList = () => navigate(absolutePath(`${ROUTES.transformations}?view=transformations`));

  return (
    <>
      {detailTopbar({
        canCancel,
        canStart,
        canWrite,
        latest,
        latestStep,
        cancelling: cancelRun.isPending,
        runNowDisabled: runNowReason !== null,
        runNowReasonId: runNowReasonShown ? RUN_NOW_REASON_ID : undefined,
        onCancel: () => void doCancel(),
        onRunNow: openRunNow,
        runAllDisabled: pageBlock !== null,
        runAllReasonId: runNowReasonShown && pageBlock !== null ? RUN_NOW_REASON_ID : undefined,
        onRunAll: () => setRunAllOpen(true),
        onEdit: () => setEditing(true),
      })}

      {breadcrumb(row.name)}

      <div className="flex flex-col gap-4">
        {runNowReasonShown ? (
          <div id={RUN_NOW_REASON_ID}>
            <SkyText size={200} color="muted" block testId="run-transformations-now-reason">
              {runNowReason}
            </SkyText>
          </div>
        ) : null}

        <SkyCard appearance="outline" testId="transformation-header">
          {headerTitleRow(row, facts, namesById, zone)}

          {stateRow({ row, facts, latest, latestStep, zone })}

          {graphRun ? kpiCaption(graphRun, facts, zone) : null}
          {graphRun ? kpiGrid(row, graphRun, facts, rowsSum) : null}
        </SkyCard>

        {!repoBound && graphData
          ? unboundBar(
              navigate,
              canAny("instance.binding.read", "instance.binding.write", "dbt_jobs.write"),
            )
          : null}

        <SkyTabList
          testId="transformation-tabs"
          selectedValue={tab}
          tabs={TABS.map((value) => ({ value, label: TAB_LABELS[value] }))}
          onTabSelect={(value) => selectTab(value as DetailTab)}
        />

        <div
          role="tabpanel"
          aria-label={TAB_LABELS[tab]}
          data-testid={`transformation-tabpanel-${tab}`}
          className="flex flex-col gap-4"
        >
          {tabPanelContent({
            tab,
            row,
            graph,
            graphRun,
            attribution,
            latest,
            canStart,
            runNowBlocked: runNowReason !== null,
            noRunCause: noRunCauseOf(row, repoBound),
            onRunNow: openRunNow,
            navigate,
            runs,
            runsLimit,
            setRunsLimit,
          })}
        </div>
      </div>

      {canWrite && editing ? (
        <DbtJobDrawer
          transformation={row}
          others={all.data ?? []}
          onClose={() => setEditing(false)}
          // The drawer is a light edit: a save goes back to the list, as the
          // owner expected, rather than leaving a page that still describes
          // the last run's selector.
          // Session 11 (P5): back to the Transformations view, the list the row is in (the
          // landing's default view is Models since ruling §8.1).
          onSaved={backToList}
          onDeleted={backToList}
          confirmForPath={ROUTES.transformations}
        />
      ) : null}

      {canStart && runAllOpen ? (
        <RunDbtJobsDialog
          onOpenChange={setRunAllOpen}
          transformations={all.data ?? []}
          bundle={bundle}
          runInFlight={latest.isActive}
        />
      ) : null}

      {canStart && runNowOpen ? (
        <RunDbtJobsDialog
          onOpenChange={setRunNowOpen}
          transformations={all.data ?? []}
          bundle={bundle}
          runInFlight={latest.isActive}
          onlyJobId={row.id}
        />
      ) : null}
    </>
  );
}
